import React, {useEffect, useRef, useState, useCallback} from 'react';
import UserService from '@services/user.service';
import {useRouter} from 'next/router';
import {useDoctorStore} from '@stores/useDoctorStore';
import {useMasterDataStore} from '@stores/useMasterDataStore';
import FilterInput from '@components/common/FilterInput';
import FilterSelect from '@components/common/FilterSelect';
import FilterButton from '@components/common/FilterButton';
import LinkSpecialistDoctorDetail from '@components/common/Link/LinkSpecialistDoctorDetail';
import LoadingNavigation from '@components/common/Loading/LoadingNavigation';
import PopupAppointment from './PopupAppointment';
import BookingSuccess from '../Home/BookingSuccess';
import {getLocaleContent} from '@helper/locale-content';
import {safeHtml} from '@helper/sanitize-html';

export default function SpecialistDoctorHome({businessId}) {
    const router = useRouter();
    const {locale} = router;

    const departments = useMasterDataStore(s => s.departments);
    const businessData = useMasterDataStore(s => s.businessData);
    const educationLevel = useMasterDataStore(s => s.educationLevel);
    const fetchAllMasterData = useMasterDataStore(s => s.fetchAllMasterData);

    const searchTimeoutRef = useRef(null);
    const doctors = useDoctorStore(s => s.doctors);
    const pageIndex = useDoctorStore(s => s.pageIndex);
    const hasMore = useDoctorStore(s => s.hasMore);
    const isLoading = useDoctorStore(s => s.isLoading);
    const filters = useDoctorStore(s => s.filters);
    const setFiltersStore = useDoctorStore(s => s.setFilters);
    const setDoctorsStore = useDoctorStore(s => s.setDoctors);
    const appendDoctorsStore = useDoctorStore(s => s.appendDoctors);
    const resetFiltersStore = useDoctorStore(s => s.resetFilters);
    const setLoadingStore = useDoctorStore(s => s.setLoading);
    const hasActiveFilters = useDoctorStore(s => s.hasActiveFilters);

    const [showAppointment, setShowAppointment] = useState(false);
    const [selectedDoctor, setSelectedDoctor] = useState(null);

    // State quản lý modal thành công
    const [showSuccess, setShowSuccess] = useState(false);
    const [bookingCode, setBookingCode] = useState('');

    // Lấy danh sách bác sĩ
    const fetchDoctors = useCallback(
        (page = 1, isLoadMore = false) => {
            setLoadingStore(true);
            const doctorService = new UserService();
            const currentFilters = useDoctorStore.getState().filters;

            doctorService
                .getUserList({
                    locale,
                    businessId: currentFilters.businessId,
                    pageSize: 10,
                    pageIndex: page,
                    keyword: currentFilters.keyword,
                    departmentId: currentFilters.departmentId,
                    educationLevelId: currentFilters.educationLevelId,
                })
                .then(res => {
                    const payload = res.data?.data || res.data || res;
                    const items = payload.items || [];
                    const total = payload.totalItems || 0;

                    if (isLoadMore) appendDoctorsStore(items, total, page, 10);
                    else setDoctorsStore(items, total, page, 10);
                })
                .catch(err => {
                    console.error(err);
                    setLoadingStore(false);
                });
        },
        [setLoadingStore, setDoctorsStore, appendDoctorsStore, locale],
    );
    // Dùng useEffect để gọi khi locale thay đổi
    useEffect(() => {
        fetchDoctors(); // gọi lại khi locale hoặc filters thay đổi
    }, [locale]);

    // handle navigation đến theo bệnh viện.
    useEffect(() => {
        if (!businessId) return;
        handleFilterChange('business', businessId);
    }, [businessId]);

    // Xử lý tìm kiếm
    const handleSearchChange = useCallback(
        value => {
            setFiltersStore({keyword: value});

            if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
            if (value.length >= 4 || value.length === 0) {
                searchTimeoutRef.current = setTimeout(() => {
                    fetchDoctors(1, false);
                }, 500);
            }
        },
        [setFiltersStore, fetchDoctors],
    );

    const handleEnterSearch = useCallback(
        value => {
            if (value.length) {
                setFiltersStore({keyword: value});
                fetchDoctors(1, false);
            }
        },
        [setFiltersStore, fetchDoctors],
    );

    // Xử lý thay đổi bộ lọc
    const handleFilterChange = useCallback(
        (filterType, value) => {
            const filterMap = {
                department: 'departmentId',
                education: 'educationLevelId',
                business: 'businessId',
            };
            const filterKey = filterMap[filterType];
            if (!filterKey) return;

            setFiltersStore({[filterKey]: value || ''});
            fetchDoctors(1, false);
        },
        [setFiltersStore, fetchDoctors],
    );

    // Xóa bộ lọc
    const handleClearFilters = useCallback(() => {
        resetFiltersStore();
        fetchDoctors(1, false);
    }, [resetFiltersStore, fetchDoctors]);

    // Lấy dữ liệu ban đầu
    useEffect(() => {
        if (!locale) return;
        fetchAllMasterData(locale);
    }, [locale, fetchAllMasterData]);

    useEffect(() => {
        if (!locale) return;
        setFiltersStore({locale});
        if (!doctors?.length) fetchDoctors(1, false);
    }, [locale, setFiltersStore, doctors?.length, fetchDoctors]);

    const handleLoadMore = useCallback(() => {
        if (hasMore && !isLoading) {
            fetchDoctors(pageIndex + 1, true);
        } else {
            console.log('No more doctors to load or currently loading.');
        }
    }, [hasMore, isLoading, pageIndex, fetchDoctors]);

    // Comment sau dùng lại nếu đổi ý.

    // Infinite scroll effect
    // useEffect(() => {
    //     const handleScroll = () => {
    //         if (
    //             window.innerHeight + document.documentElement.scrollTop >=
    //             document.documentElement.offsetHeight - 1000
    //         ) {
    //             handleLoadMore();
    //         }
    //     };

    //     window.addEventListener('scroll', handleScroll);
    //     return () => window.removeEventListener('scroll', handleScroll);
    // }, [handleLoadMore]);

    return (
        <section className="doctors-team">
            <h2>{getLocaleContent(locale, 'page.specialist-doctor.title')}</h2>
            <div className="filter-bars-header">
                <div className="filter-bars">
                    <div className="filter-bar">
                        <div className="filter-item">
                            <span className="filter-label">
                                {getLocaleContent(locale, 'page.specialist-doctor.hospital-label')}
                            </span>
                            <FilterSelect
                                placeholder={getLocaleContent(locale, 'page.specialist-doctor.choose-hospital')}
                                value={filters.businessId}
                                onChange={value => handleFilterChange('business', value)}
                                options={businessData.map(biz => ({
                                    value: biz.business_id,
                                    label: biz.business_name,
                                }))}
                            />
                        </div>

                        <div className="filter-item">
                            <span className="filter-label">
                                {getLocaleContent(locale, 'page.specialist-doctor.department-label')}
                            </span>
                            <FilterSelect
                                placeholder={getLocaleContent(locale, 'page.specialist-doctor.choose-department')}
                                value={filters.departmentId}
                                onChange={value => handleFilterChange('department', value)}
                                options={departments.map(dep => ({
                                    value: dep.department_id,
                                    label: dep.department_name,
                                }))}
                            />
                        </div>

                        <div className="filter-item">
                            <span className="filter-label">
                                {getLocaleContent(locale, 'page.specialist-doctor.education-label')}
                            </span>
                            <FilterSelect
                                placeholder={getLocaleContent(locale, 'page.specialist-doctor.choose-education')}
                                value={filters.educationLevelId}
                                onChange={value => handleFilterChange('education', value)}
                                options={educationLevel.map(level => ({
                                    value: level.education_level_id,
                                    label: level.education_level_name,
                                }))}
                            />
                        </div>

                        <div className="filter-item">
                            <span className="filter-label">
                                {getLocaleContent(locale, 'page.specialist-doctor.doctor-name-label')}
                            </span>
                            <FilterInput
                                placeholder={getLocaleContent(locale, 'page.specialist-doctor.doctor-name-placeholder')}
                                value={filters.keyword}
                                onChange={e => handleSearchChange(e.target.value)}
                                onEnter={handleEnterSearch}
                            />
                        </div>

                        {hasActiveFilters() && (
                            <div className="filter-item">
                                <FilterButton onClick={handleClearFilters}>
                                    {getLocaleContent(locale, 'page.specialist-doctor.clear-filter')}
                                </FilterButton>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <div className="doctors-grid">
                {doctors.length === 0 && !isLoading && (
                    <div>{getLocaleContent(locale, 'page.specialist-doctor.no-doctors')}</div>
                )}
                {doctors.map((user, index) => (
                    <LinkSpecialistDoctorDetail key={user.user_id || index} doctor={user}>
                        <article className="doccard">
                            <div className="doccard__media">
                                <img src={user.image_url} alt={user.full_name} />
                            </div>
                            <div className="doccard__body">
                                <span className="doccard__tag">{user.education_level_name || ' '}</span>
                                <h3 className="doccard__name">{user.full_name}</h3>
                                <p className="doccard__meta">
                                    {user.position_name}
                                    {' - '}
                                    {user.department_name}
                                    {user?.business_name && (
                                        <>
                                            {' '}
                                            <br />
                                            {user.business_name}
                                        </>
                                    )}
                                    {user?.expertise && (
                                        <>
                                            <br />
                                            {getLocaleContent(locale, 'page.doctors.expertise')} {user.expertise}
                                        </>
                                    )}
                                </p>
                                <button
                                    className="doccard__cta"
                                    onClick={e => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setSelectedDoctor(user);
                                        setShowAppointment(true);
                                    }}>
                                    <img className="icon" src="/bw_image/ic_calendar_w.png" alt="" />
                                    {getLocaleContent(locale, 'page.doctors.booking')}
                                </button>
                            </div>
                            <div className="doccard__overlay">
                                <div className="doccard__ov-wrap">
                                    {user.education_level_name && (
                                        <span className="doccard__tag">{user.education_level_name}</span>
                                    )}
                                    <h3 className="doccard__name">{user.full_name}</h3>
                                    <p className="doccard__meta">
                                        {user.position_name} {' - '} {user.department_name}
                                    </p>
                                    <p className="doccard__meta">
                                        {user?.business_name && <>{user.business_name}</>}
                                        {user?.expertise && (
                                            <>
                                                <br />
                                                {getLocaleContent(locale, 'page.doctors.expertise')} {user.expertise}
                                            </>
                                        )}
                                    </p>
                                    <h4 className="doccard__overlay-title">
                                        {getLocaleContent(locale, 'page.doctors.experience-department')}
                                    </h4>
                                    <div
                                        className="doccard__list"
                                        dangerouslySetInnerHTML={safeHtml(user.about_me_short)}></div>
                                    <button
                                        className="doccard__cta"
                                        onClick={e => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            setSelectedDoctor(user);
                                            setShowAppointment(true);
                                        }}>
                                        <img className="icon" src="/bw_image/ic_calendar_w.png" alt="" />
                                        {getLocaleContent(locale, 'page.doctors.booking')}
                                    </button>
                                </div>
                            </div>
                        </article>
                    </LinkSpecialistDoctorDetail>
                ))}
            </div>
            {hasMore && !isLoading && (
                <button type="button" className="load-more" onClick={handleLoadMore}>
                    {getLocaleContent(locale, 'page.about-us.more')}
                    <img src="/bw_image/chevron-down-double.svg" alt="" className="icon" />
                </button>
            )}
            {isLoading && <LoadingNavigation size="large" color="#006940" style={{marginBottom: '40px'}} />}

            {showAppointment && (
                <PopupAppointment
                    onClose={() => {
                        setShowAppointment(false);
                        setSelectedDoctor(null);
                    }}
                    selectedDoctor={selectedDoctor}
                    onSuccess={code => {
                        setBookingCode(code);
                        setShowSuccess(true);
                    }}
                />
            )}

            {/* Modal thành công */}
            <BookingSuccess
                open={showSuccess}
                bookingCode={bookingCode}
                onClose={() => {
                    setShowSuccess(false);
                    setBookingCode('');
                }}
            />
        </section>
    );
}
